import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { Close as CloseIcon, Update as UpdateIcon, Done as DoneIcon } from '@mui/icons-material';
import { Avatar, Box, Button, List, ListItem, Typography } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { confirmAlert } from 'react-confirm-alert';
import useRescuerDashboardActions from '../../../../../hooks/useRescuerDashboardActions';
import useRescuerRescueActions from '../../../../../hooks/useRescuerRescueActions';
import { getMyRescues } from '../../../../../helpers/RescuesHelper';
import RescueMobileCard from '../../../../Rescue/RescueMobileCard';
import ButtonWithLoading from '../../../../../components/ButtonWithLoading';
import useActiveUser from '../../../../../hooks/useActiveUser';
import { Colors } from '../../../../../assets/theme/Colors';
import { StyledSubHeader } from '../myFrus/MyFrusMobile';
import ConfirmationDialog from '../../../../../components/ConfirmationDialog';

const containerSx = {
  position: 'relative',
  height: '100%',
  width: '100%',
  overflow: 'auto',
  backgroundColor: 'background.default',
};

const listSubItemSx = {
  padding: 1,
};

const ulSx = {
  marginBottom: '16px',
  backgroundColor: 'inherit',
  padding: 0,
};

const ReleaseRescueConfirmationDialog = ({ buttons, onClose, title, message }) => (
  <ConfirmationDialog buttons={buttons} closeDialog={onClose} title={title} message={message} />
);

const customFooter = () => <Box sx={{ pb: 9 }} />;

const MyRescuesMobile = () => {
  let rescuesInflight = {};
  const activeUser = useActiveUser();
  const { fetchMyFrusTab } = useRescuerDashboardActions();
  const { releaseRescue, rescueUnclaim, rescueUnadopt } = useRescuerRescueActions();
  const { formatMessage } = useIntl();
  const rescuesEntities = useSelector(state => state.entities.rescues);
  const rescuerRescues = getMyRescues(activeUser.id, rescuesEntities, true); // with closed rescues
  const isLoading = get(rescuesEntities, `claimedAndAdoptedIds[${activeUser.id}].inflight`, true);
  const myRescuesCount = rescuerRescues.length;

  useEffect(() => {
    fetchMyFrusTab(false, false);
  }, [activeUser.id, fetchMyFrusTab]);

  if (!isLoading && rescuerRescues.length === 0) {
    return (
      <Box sx={containerSx}>
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            WebkitTransform: 'translate(-50%, -50%)',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography color="textSecondary" variant="h5">
            {formatMessage({ id: 'rescuer-dashboard-mobile.my-rescues.no-claimed-rescues' })}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <List sx={containerSx} subheader={<li />}>
        <li style={{ backgroundColor: 'inherit' }}>
          <ul style={ulSx}>
            <ListItem sx={listSubItemSx}>
              <RescueMobileCard isLoading />
            </ListItem>
            <ListItem sx={listSubItemSx}>
              <RescueMobileCard isLoading />
            </ListItem>
            <ListItem sx={listSubItemSx}>
              <RescueMobileCard isLoading />
            </ListItem>
          </ul>
        </li>
      </List>
    );
  }

  const handleUnadopt = (rescue) =>
    confirmAlert({
      title: 'Are you sure?',
      buttons: [
        {
          variant: 'outlined',
          label: 'Never mind',
          color: 'primary',
          size: 'small',
          onClick: () => {},
        },
        {
          variant: 'contained',
          label: 'Unadopt',
          color: 'primary',
          size: 'small',
          onClick: () => rescueUnadopt(rescue, true),
        },
      ],
      message: 'Unadopting this rescue will release all rescues from this adoption. Do you want to continue?',
      customUI: ({ title, message, onClose, buttons }) => (
        <ConfirmationDialog
          buttons={buttons}
          closeDialog={onClose}
          title={title}
          message={message}
        />
      ),
    });

  const handleReleaseClick = (rescue) =>
    confirmAlert({
      title: `Are you sure?`,
      buttons: [
        {
          variant: 'outlined',
          label: 'Never mind',
          color: 'primary',
          onClick: () => {},
        },
        {
          variant: 'contained',
          label: 'Release',
          color: 'primary',
          onClick: () => releaseRescue(rescue, true),
        },
      ],
      message: 'Releasing this rescue may result in this food going to waste, but we understand that life happens!',
      customUI: ReleaseRescueConfirmationDialog,
    });

  const handleUnclaimClick = (rescue) =>
    confirmAlert({
      title: `Are you sure?`,
      buttons: [
        {
          variant: 'outlined',
          label: 'Never mind',
          color: 'primary',
          onClick: () => {},
        },
        {
          variant: 'contained',
          label: 'Release',
          color: 'primary',
          onClick: () => rescueUnclaim(rescue, true),
        },
      ],
      message: 'Releasing this rescue may result in this food going to waste, but we understand that life happens!',
      customUI: ReleaseRescueConfirmationDialog,
    });

  return (
    <Box display="flex" flexDirection="column" width={1} pt={1}>
      {!isLoading && rescuerRescues.length > 0 && (
        <Box px={1}>
          <StyledSubHeader
            color={Colors.rescues.claimed.color}
            // component={RouterLink}
            // to={generatePath(routes.dashboardRescuerRescues, { tab: RESCUER_MOBILE_DASHBOARD_TAB_MY_RESCUES })}
            text={formatMessage({ id: 'rescuer-dashboard-mobile.my-rescues.upcoming-rescues' })}
            subText={formatMessage({ id: 'rescuer-dashboard-mobile.my-rescues.upcoming-rescues-sub' })}
            icon={
              <Avatar sx={{ backgroundColor: Colors.rescues.claimed.color, filter: 'brightness(0.9)' }}>
                <UpdateIcon />
              </Avatar>
            }
          />
        </Box>
      )}
      <Virtuoso
        style={{ width: '100%', height: '100%' }}
        components={{
          Footer: customFooter,
        }}
        totalCount={myRescuesCount}
        itemContent={index => {
          const rescue = rescuerRescues[index];
          const rescueDateTime = moment(`${rescue.date} ${rescue.pickup_begin}`, 'YYYYMMDD HH:mm:ss');
          const isRescuePast = moment().isSameOrAfter(rescueDateTime, 'second');
          return (
            <Box key={`rescue-${rescue.id}`} sx={listSubItemSx}>
              <RescueMobileCard
                rescue={rescue}
                isCancelled={rescue.cancelled_by_id !== null}
                isClosed={rescue.closed_by_id !== null}
              >
                {rescue.closed_by_id === null && rescue.cancelled_by_id === null && !isRescuePast &&
                  (rescue.adopter_id === activeUser.id ? (
                    <Box display="grid" gap={1}>
                      <ButtonWithLoading
                        fullWidth
                        startIcon={<CloseIcon fontSize="small" />}
                        isLoading={rescuesInflight[rescue.id] && rescuesInflight[rescue.id] === 'release'}
                        disabled={!!rescuesInflight[rescue.id]}
                        onClick={() => handleReleaseClick(rescue)}
                        size="large"
                        color="primary"
                        variant="contained"
                      >
                        {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.release-rescue' })}
                      </ButtonWithLoading>
                      <ButtonWithLoading
                        fullWidth
                        startIcon={<CloseIcon fontSize="small" />}
                        isLoading={rescuesInflight[rescue.id] && rescuesInflight[rescue.id] === 'unadopt'}
                        disabled={!!rescuesInflight[rescue.id]}
                        onClick={() => handleUnadopt(rescue)}
                        size="large"
                        color="primary"
                        variant="outlined"
                      >
                        {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.unadopt' })}
                      </ButtonWithLoading>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="stretch">
                      <ButtonWithLoading
                        fullWidth
                        startIcon={<CloseIcon fontSize="small" />}
                        isLoading={rescuesInflight[rescue.id] && rescuesInflight[rescue.id] === 'unclaim'}
                        disabled={!!rescuesInflight[rescue.id]}
                        size="large"
                        color="primary"
                        variant="contained"
                        onClick={() => handleUnclaimClick(rescue)}
                      >
                        {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.release-rescue' })}
                      </ButtonWithLoading>
                    </Box>
                  ))}
                  {rescue.closed_by_id === null && rescue.cancelled_by_id === null && isRescuePast && (
                    <Box display="grid" gap={1}>
                      <Button
                        startIcon={<DoneIcon />}
                        size="large"
                        color="primary"
                        variant="contained"
                        component={RouterLink}
                        to={`/rescue/close/${rescue.id}`}
                      >
                        {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.i-did-it' })}
                      </Button>

                      <Button
                        startIcon={<CloseIcon />}
                        size="large"
                        color="primary"
                        variant="outlined"
                        component={RouterLink}
                        to={`/rescue/close/noshow/${rescue.id}`}
                      >
                        {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.i-could-not-make-it' })}
                      </Button>
                    </Box>
                  )}
              </RescueMobileCard>
            </Box>
          );
        }}
      />
    </Box>
  );
};

export default MyRescuesMobile;
